/**
 * Runtime site slug maps derived from the unified sections content collection.
 *
 * Each site index.md carries `translationKey: <canonical-key>` in its frontmatter.
 * This module reads those values once, caches them, and exposes two lookups:
 *   - slugToCollection('use')          → 'gebruik'  (camelCased for queryCollection)
 *   - getLocaleSlug('gebruik', 'en')   → 'use'
 *
 * No slug data is hardcoded here — all mappings come from content files.
 *
 * When site index.md files lack translationKey (no explicit cross-locale grouping),
 * getLocaleSlug and hasSiteInLocale fall back to ordinal-position matching: sites at
 * the same numeric position (1.*, 2.*, 3.*) across locales are treated as equivalents.
 * slugToCollection is NOT affected by ordinal fallback — it always uses each slug's own
 * canonical so queryCollection targets the correct per-locale-slug collection.
 */

type SlugMaps = {
  slugToCanonical: Record<string, string>
  canonicalLocaleSlug: Record<string, Record<string, string>>
  /** ordinal (1/2/3) → locale → slug, used as fallback when no translationKey grouping */
  ordinalLocaleSlug: Record<string, Record<string, string>>
}

let _cache: Promise<SlugMaps> | null = null

async function initSlugMaps(): Promise<SlugMaps> {
  if (_cache !== null) return _cache

  _cache = (async () => {
  // Query unified sections collection and filter for docs site index pages
  const allPages = await (await queryPages('sections')).select('stem', 'translationKey').all()

  const slugToCanonical: Record<string, string> = {}
  const canonicalLocaleSlug: Record<string, Record<string, string>> = {}
  const ordinalLocaleSlug: Record<string, Record<string, string>> = {}

  for (const item of allPages) {
    // stem format: "{locale}/{docs|N.docs}/{N.site-dir}/index" — both the docs
    // segment and the site-dir segment can carry a numeric ordinal prefix.
    const parts = ((item.stem ?? '') as string).split('/')
    if (parts.length !== 4 || parts[3] !== 'index') continue
    if (parts[1] !== 'docs' && !/^\d+\.docs$/.test(parts[1] ?? '')) continue
    const locale = parts[0]
    const siteDirPart = parts[2] ?? ''
    const slug = siteDirPart.replace(/^\d+\./, '')
    const ordinal = siteDirPart.match(/^(\d+)\./)?.[1] ?? ''
    const canonical: string = item.translationKey ?? slug
    if (!slug || !locale) continue

    slugToCanonical[slug] = canonical
    if (!canonicalLocaleSlug[canonical]) canonicalLocaleSlug[canonical] = {}
    canonicalLocaleSlug[canonical][locale] = slug

    if (ordinal) {
      if (!ordinalLocaleSlug[ordinal]) ordinalLocaleSlug[ordinal] = {}
      ordinalLocaleSlug[ordinal][locale] = slug
    }
  }

  return { slugToCanonical, canonicalLocaleSlug, ordinalLocaleSlug }
  })()
  _cache.catch(() => { _cache = null })
  return _cache
}

/**
 * Resolves a (possibly locale-specific) site slug to the camelCase Nuxt Content
 * collection name. E.g. 'use' → 'gebruik', 'nutzen' → 'gebruik', 'release-notes' → 'releaseNotes'.
 *
 * Returns `null` when `slug` is not a known docs site (e.g. a sub-site slug like
 * `regelgeving-europa` or `pdf-herkenning`). Previously this fell back to the raw
 * slug, producing a phantom collection name with no backing table — callers then
 * issued `queryPages(<phantom>)` which 404'd the content endpoint (during
 * prerender: floods of `[unhandledRejection] POST /__nuxt_content/<col>/query`).
 * Callers must treat `null` as "no such collection" and skip the query.
 */
export async function slugToCollection(slug: string): Promise<string | null> {
  const { slugToCanonical } = await initSlugMaps()
  const canonical = slugToCanonical[slug]
  if (canonical === undefined) return null
  // MUST match the collection-name derivation in `discoverDocsSiteCollections`
  // (contentConfigUtils.ts): camelCase across every non-alnum separator and
  // strip the rest, so a canonical with a `/` (e.g. `bouwen/api-reference`)
  // yields the same valid identifier the collection was registered under.
  return canonical
    .replace(/[^a-zA-Z0-9]+([a-zA-Z0-9])/g, (_, c: string) => c.toUpperCase())
    .replace(/[^a-zA-Z0-9]/g, '')
}

/**
 * Returns the site dir slug for a canonical key in a given locale.
 * Falls back to ordinal-position matching when no translationKey grouping exists.
 * E.g. getLocaleSlug('gebruiken', 'en') → 'use'
 */
export async function getLocaleSlug(siteSlug: string, locale: string): Promise<string> {
  const { slugToCanonical, canonicalLocaleSlug, ordinalLocaleSlug } = await initSlugMaps()
  const canonical = slugToCanonical[siteSlug] ?? siteSlug
  // Explicit translationKey-based mapping
  const explicit = canonicalLocaleSlug[canonical]?.[locale]
  if (explicit) return explicit
  // Ordinal fallback: find the ordinal group containing siteSlug, return the target locale's slug
  for (const localeSlugMap of Object.values(ordinalLocaleSlug)) {
    if (Object.values(localeSlugMap).includes(siteSlug)) {
      return localeSlugMap[locale] ?? siteSlug
    }
  }
  return siteSlug
}

/**
 * Returns true when a site has content in the given locale.
 * Falls back to ordinal-position matching when no translationKey grouping exists.
 */
export async function hasSiteInLocale(siteSlug: string, locale: string): Promise<boolean> {
  const { slugToCanonical, canonicalLocaleSlug, ordinalLocaleSlug } = await initSlugMaps()
  const canonical = slugToCanonical[siteSlug] ?? siteSlug
  if (canonicalLocaleSlug[canonical]?.[locale] !== undefined) return true
  // Ordinal fallback
  for (const localeSlugMap of Object.values(ordinalLocaleSlug)) {
    if (Object.values(localeSlugMap).includes(siteSlug)) {
      return localeSlugMap[locale] !== undefined
    }
  }
  return false
}
